import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authApi } from '@/services/auth';
import { toast } from '@/store/ui';
import { Button, Field, Input } from '@/components/ui';
import { handleFormError } from '../shared/forms';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(10, 'At least 10 characters').regex(/[a-zA-Z]/, 'Include a letter').regex(/\d/, 'Include a number'),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });
type Form = z.infer<typeof schema>;

export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
  const { register, handleSubmit, formState, setError, reset } = useForm<Form>({ resolver: zodResolver(schema) });
  const onSubmit = handleSubmit(async (v) => {
    try {
      await authApi.changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword });
      await authApi.me();
      toast.success('Password changed', 'Your other sessions have been signed out.');
      reset();
      onDone?.();
    } catch (e) {
      handleFormError(e, setError);
    }
  });
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <Field label="Current password" error={formState.errors.currentPassword?.message}>{(p) => <Input {...p} type="password" autoComplete="current-password" {...register('currentPassword')} />}</Field>
      <Field label="New password" error={formState.errors.newPassword?.message}>{(p) => <Input {...p} type="password" autoComplete="new-password" {...register('newPassword')} />}</Field>
      <Field label="Confirm new password" error={formState.errors.confirm?.message}>{(p) => <Input {...p} type="password" autoComplete="new-password" {...register('confirm')} />}</Field>
      <Button type="submit" loading={formState.isSubmitting}>Update password</Button>
    </form>
  );
}

export function ChangePasswordPage() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 shadow-card">
        <h1 className="text-xl font-semibold">Set a new password</h1>
        <p className="mb-5 mt-1 text-sm text-ink-muted">Your administrator gave you a temporary password. Choose your own to continue.</p>
        <ChangePasswordForm onDone={() => navigate('/', { replace: true })} />
      </div>
    </div>
  );
}
